<template>
  <div id="app" :class="{'app_homepage': isLandingPage || isAuthorizationPage}">
    <header-el :class="{'header_homepage': isAuthorizationPage}"/>
    <transition name="fade" appear>
      <platform-notifications v-if="platformNotifications.length"/>
    </transition>
    <router-view :class="{'no-scroll': showLoginModal}" />
    <footer-el :class="{'footer_homepage': isAuthorizationPage}"/>
    <side-panel
      class="side-panel"
      :class="{'side-panel_homepage': isLandingPage}"
      v-if="!isAuthorizationPage"
    />
    <login-modal  v-if="showLoginModal" @click="onOverlayClicked" />
    <user-notifications />
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import emitter from 'tiny-emitter/instance';
import { notificationsUpdateMinInterval } from 'src/utils/config';
import { checkCurrentDeviceIsMobile } from 'src/utils/helpers';
import HeaderEl from 'Layout/HeaderEl';
import FooterEl from 'Layout/FooterEl';
import SidePanel from 'Layout/SidePanel';
import LoginModal from 'Components/loginTools/LoginModal';
import PlatformNotifications from 'Components/PlatformNotifications';
import UserNotifications from 'Components/userNotifications';

function disablePointerEventsOnScroll() {
  const body = document.body;
  let timer;

  window.addEventListener('scroll', () => {
    clearTimeout(timer);
    if (!body.classList.contains('disable-hover')) {
      body.classList.add('disable-hover');
    }

    timer = setTimeout(() => {
      body.classList.remove('disable-hover');
    }, 500);
  }, false);
}

export default {
  components: {
    LoginModal,
    HeaderEl,
    FooterEl,
    SidePanel,
    PlatformNotifications,
    UserNotifications,
  },
  name: 'app',
  mounted() {
    this.$router.afterEach(() => {
      this.$refs.sideMenu && this.$refs.sideMenu.close(); // eslint-disable-line
    });
    this.autoUpdatePlatformNotifications();
    emitter.on('error', (text) => {
      this.$toasted.error(this.beautifyErrorMessage(text), {
        position: 'top-right',
        duration: 4000,
        action: [
          {
            text: '×',
            onClick: (e, toastObject) => {
              toastObject.goAway(0);
            },
          },
        ],
      });
    });
  },
  computed: {
    ...mapState('settings', ['showLoginModal']),
    ...mapState('userData', ['loginInfo', 'platformNotifications']),
    ...mapState('platformNotifications', ['platformNotifications']),
    ...mapGetters('userData', ['userIsAdmin', 'settingsIsLoaded']),
    isLandingPage() {
      return this.$route.meta.isLandingPage;
    },
    isAuthorizationPage() {
      return this.$route.meta.isAuthorizationPage;
    },
  },
  watch: {
    loginInfo(token) {
      if (token && !this.settingsIsLoaded) {
        this.updateUserSettings();
      } else {
        this.notifyAboutLoggedOut();
      }
    },
  },
  created() {
    if (this.loginInfo && !this.settingsIsLoaded) {
      this.updateUserSettings();
    }
    if (checkCurrentDeviceIsMobile()) {
      disablePointerEventsOnScroll();
    }
  },
  methods: {
    ...mapActions('userData', [
      'logOut',
      'updateUserSettings',
    ]),
    ...mapActions('platformNotifications', ['updatePlatformNotifications']),
    beautifyErrorMessage(text) {
      const textWithFirstCapitalLetter = text[0].toUpperCase() + text.slice(1);
      return textWithFirstCapitalLetter;
    },
    toggleSideMenu() {
      this.$refs.sideMenu.toggle();
    },
    autoUpdatePlatformNotifications() {
      setTimeout(() => {
        const promises = [];
        if (this.loginInfo) {
          promises.push(this.updatePlatformNotifications({ dontShowErrors: true }));
        }
        Promise.all(promises).finally(this.autoUpdatePlatformNotifications);
      }, notificationsUpdateMinInterval);
    },
    onOverlayClicked() {
      this.toogleModalLogin({ val: false });
    },
    notifyAboutLoggedOut() {
      this.$toasted.error('You have been logged out', {
        position: 'top-right',
        duration: 4000,
        action: [
          {
            text: '×',
            onClick: (e, toastObject) => {
              toastObject.goAway(0);
            },
          },
        ],
      });
    },
  },
};
</script>

<style lang="scss">
.disable-hover,
.disable-hover * {
  pointer-events: none !important;
}

.no-scroll {
  overflow-y: hidden;
}
.nav-buttons {
  text-align: right;
  display: block !important;
}
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  height: 100%;
  background-color: rgba(0, 0, 0, .5);
  display: table;
  transition: opacity .3s ease;
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}

.modal-container {
  width: 360px;
  margin: 0px auto;
  padding: 20px 30px;
  background-color: $white-color2;
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .33);
  transition: all .3s ease;
  font-family: Helvetica, Arial, sans-serif;
}

.modal-header h3 {
  margin-top: 0;
  color: $green-color2;
}

.modal-body {
  margin: 20px 0;
}

.modal-default-button {
  float: right;
}

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
.vertical-middle {
  vertical-align: middle !important;
}
body, #app {
  height: 100%;
}
#app {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1 0 auto;
  margin-left: $closed-sidepanel-width;
  position: relative;
}
.app_homepage {
  margin-left: 0 !important;
}
.side-panel {
  &_homepage {
    display: none;
    @include mobileStyles {
      display: block;
    }
  }
}

.header {
  flex-shrink: 0;
}
.footer {
  flex: 0 0 auto;
}
.spinner-container {
  display: flex;
  height: 100vh;
  width: 100%;
}
.logo {
  align-self: center;
  color: $white-color2 !important;
  text-decoration: none !important;;

  &:hover,
  &:active {
    color: $white-color2 !important;
  }
}
.menu-btn {
  align-self: center;
}
.toasted.primary.info {
  background: $blue-color1 !important;
  .action:hover,
  .action:active,
  .action:visited {
    color: $white-color2;
  }
}
.toasted-container.top-right {
  right: 10px;
  top: 0;
}
.toasted-container .toasted {
  margin-top: 8px;
  top: 37px;
}
.toasted.primary.error {
  background: $white-color2 url("~src/assets/svg/attention.svg") no-repeat !important;
  background-position: 24px 23px !important;
  position: relative;
  border: 1px solid $red-color3;
  box-sizing: border-box;
  box-shadow: 3px 3px 10px $black-color4;
  border-radius: 5px;
  padding: 21px 47px 21px 76px;
  line-height: 17px;
  max-width: 487px;
  min-height: 80px;
  @include font(13px, $black-color1, 500);

  .action.ripple {
    position: absolute;
    top: -1px;
    right: 7px;
    font-size: 39px;
    font-weight: 100;
    &:hover {
      text-decoration: none;
    }
    color: $red-color3;
  }
}
.toasted-container {
  flex-direction: column-reverse !important;
}
.link {
  color: $white-color2 !important;
}
* {
  &::selection {
    background: $red-color6 !important;
  }
}

  .fade-enter-active, .fade-leave-active {
    transition: opacity $transition;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

@include mobileStyles {
  #app {
    margin-left: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue